import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useLogin } from "./useAuth";

type Props = {
  appName: string;
  appTitle?: string;       // z.B. "Vimaana Planer"
  redirectAfterLogin?: string;       // default: "/" oder must_change_password → "/change-password"
};

/**
 * Standard-Login (Email + Passwort + optional TOTP), wiederverwendbar für jede App.
 * Hinweis-Text "nur per Einladung" — Apps haben keinen Self-Signup.
 */
export function LoginCard({ appName, appTitle, redirectAfterLogin = "/" }: Props) {
  const navigate = useNavigate();
  const login = useLogin(appName);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg("");
    try {
      const body: { email: string; password: string; totp_code?: string } = {
        email: email.trim().toLowerCase(),
        password,
      };
      if (needsTotp && totpCode) body.totp_code = totpCode;
      const result = await login.mutateAsync(body);
      localStorage.setItem(`vg_last_email_${appName}`, body.email);
      navigate(result.must_change_password ? "/change-password" : redirectAfterLogin, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = (err.body as { detail?: string })?.detail || "";
        if (err.status === 401 && hint === "TOTP required.") {
          setNeedsTotp(true);
          setErrorMsg("Bitte zusätzlich den 6-stelligen Code aus deiner Authenticator-App eingeben.");
        } else if (err.status === 423) {
          setErrorMsg("Konto temporär gesperrt (zu viele Fehlversuche). Bitte 15 Min warten.");
        } else if (err.status === 429) {
          setErrorMsg("Zu viele Versuche. Bitte ein paar Minuten warten.");
        } else if (err.status === 401) {
          setErrorMsg("Email oder Passwort falsch.");
        } else {
          setErrorMsg("Login fehlgeschlagen. Bitte später erneut versuchen.");
        }
      } else {
        setErrorMsg("Verbindung fehlgeschlagen.");
      }
    }
  }

  return (
    <div className="m-app login-shell">
      <div className="login-card">
        <div className="login-logo">{appTitle ?? "Vimaana"}</div>
        <div className="login-sub">Anmelden mit deiner Vimaana-Email. Zugriff nur nach Einladung.</div>

        <form onSubmit={submit} className="login-form">
          <label className="login-label">
            <span>Email</span>
            <input
              type="email" inputMode="email" autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="name@vimaanagarten.de"
              required autoFocus={!email}
            />
          </label>
          <label className="login-label">
            <span>Passwort</span>
            <input
              type="password" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              required autoFocus={!!email}
            />
          </label>
          {needsTotp ? (
            <label className="login-label">
              <span>2-Faktor-Code</span>
              <input
                type="text" inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-stellig" required autoFocus
              />
            </label>
          ) : null}
          <button
            type="submit" className="login-btn"
            disabled={login.isPending || !email || !password || (needsTotp && totpCode.length !== 6)}
          >
            {login.isPending ? "Prüfe…" : "Anmelden"}
          </button>
          {errorMsg ? <div className="login-error">{errorMsg}</div> : null}
        </form>

        <div className="login-hint">
          Noch keinen Zugang? Bitte einen Inhaber um Einladung — Self-Signup gibt es nicht.
        </div>
      </div>
    </div>
  );
}
